import React, { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { JsonForms } from "@jsonforms/react";
import {
    materialRenderers,
    materialCells,
} from "@jsonforms/material-renderers";
import { JsonFormsRendererRegistryEntry, createAjv } from "@jsonforms/core";
import { useTranslation } from "react-i18next";
import { FormEditorState } from "./types";
import { buildSchema } from "./schemaBuilder";
import {
    AttachmentReadOnlyRenderer,
    attachmentRendererTester,
} from "../../forms/attachmentRenderer";

const allRenderers: JsonFormsRendererRegistryEntry[] = [
    ...materialRenderers,
    { tester: attachmentRendererTester, renderer: AttachmentReadOnlyRenderer },
];

const ajv = createAjv();
ajv.addFormat("attachment", true);

interface PreviewPanelProps {
    editorState: FormEditorState;
}

export const PreviewPanel = ({ editorState }: PreviewPanelProps) => {
    const { t } = useTranslation();

    const schema = useMemo(() => buildSchema(editorState), [editorState]);

    const hasFields = editorState.fields.length > 0;

    return (
        <Box sx={{ p: 2, height: "100%", overflow: "auto" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t("settings.formEditor.preview")}
            </Typography>
            {hasFields ? (
                <Box sx={{ "& .MuiRadioGroup-root": { mb: 1, order: 1 } }}>
                    <JsonForms
                        schema={schema.schema}
                        uischema={schema.uiSchema}
                        data={{}}
                        renderers={allRenderers}
                        cells={materialCells}
                        config={{
                            showUnfocusedDescription: true,
                            isEditorPreview: true,
                        }}
                        ajv={ajv}
                        validationMode="ValidateAndHide"
                    />
                </Box>
            ) : (
                <Alert severity="info">
                    {t("settings.formEditor.noFields")}
                </Alert>
            )}
        </Box>
    );
};
